import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 40px 20px;
    color: ${({ theme }) => theme.text};
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 10px;
`;

const LastUpdated = styled.p`
    color: ${({ theme }) => theme.textSecondary};
    margin-bottom: 40px;
    font-style: italic;
`;

const Section = styled.section`
    margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 15px;
    color: ${({ theme }) => theme.primary};
`;

const Paragraph = styled.p`
    line-height: 1.7;
    margin-bottom: 15px;
`;

const List = styled.ul`
    margin-left: 20px;
    margin-bottom: 15px;
    
    li {
        line-height: 1.7;
        margin-bottom: 8px;
    }
`;

const TermsOfService = () => {
    return (
        <Container>
            <Title>Terms of Service</Title>
            <LastUpdated>Last Updated: February 9, 2026</LastUpdated>

            <Section>
                <Paragraph>
                    Welcome to PropNinja. By accessing or using our website at propninja.ai, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>1. Description of Service</SectionTitle>
                <Paragraph>
                    PropNinja provides real estate investment analysis tools that help users evaluate potential property investments. Our service includes property data analysis, financial calculations, and AI-powered insights.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>2. Account Registration</SectionTitle>
                <Paragraph>To use our services, you must:</Paragraph>
                <List>
                    <li>Be at least 18 years old</li>
                    <li>Provide accurate and complete registration information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Notify us immediately of any unauthorized use of your account</li>
                </List>
            </Section>

            <Section>
                <SectionTitle>3. Subscription and Payments</SectionTitle>
                <List>
                    <li>Subscriptions are billed on a recurring basis (monthly or as specified)</li>
                    <li>You may cancel your subscription at any time through your account settings</li>
                    <li>Refunds are handled on a case-by-case basis</li>
                    <li>We reserve the right to change pricing with 30 days notice</li>
                </List>
            </Section>

            <Section>
                <SectionTitle>4. Acceptable Use</SectionTitle>
                <Paragraph>You agree not to:</Paragraph>
                <List>
                    <li>Use the service for any illegal purpose</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Interfere with or disrupt the service</li>
                    <li>Share your account credentials with others</li>
                    <li>Use automated tools to access the service (bots, scrapers)</li>
                    <li>Resell or redistribute our data or analysis</li>
                </List>
            </Section>

            <Section>
                <SectionTitle>5. Disclaimer of Warranties</SectionTitle>
                <Paragraph>
                    <strong>PropNinja is provided "as is" without warranties of any kind.</strong> We do not guarantee the accuracy, completeness, or reliability of any property data or analysis. Our service is for informational purposes only and should not be considered financial, investment, or legal advice.
                </Paragraph>
                <Paragraph>
                    <strong>Important:</strong> Always conduct your own due diligence and consult with qualified professionals before making real estate investment decisions.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>6. Limitation of Liability</SectionTitle>
                <Paragraph>
                    To the maximum extent permitted by law, PropNinja shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service, including but not limited to loss of profits, data, or investment opportunities.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>7. Data Sources</SectionTitle>
                <Paragraph>
                    Our property data is aggregated from third-party sources. While we strive for accuracy, we cannot guarantee that all information is current or error-free. Property values, rental estimates, and market data are approximations and may differ from actual values.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>8. Intellectual Property</SectionTitle>
                <Paragraph>
                    All content, features, and functionality of PropNinja are owned by us and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute our content without permission.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>9. Termination</SectionTitle>
                <Paragraph>
                    We may terminate or suspend your account at any time for violations of these terms or for any other reason at our discretion. Upon termination, your right to use the service will immediately cease.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>10. Changes to Terms</SectionTitle>
                <Paragraph>
                    We reserve the right to modify these terms at any time. We will notify users of significant changes via email or through the service. Continued use after changes constitutes acceptance of the new terms.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>11. Governing Law</SectionTitle>
                <Paragraph>
                    These terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>12. Contact</SectionTitle>
                <Paragraph>
                    For questions about these Terms of Service, please contact us at support@propninja.ai.
                </Paragraph>
            </Section>
        </Container>
    );
};

export default TermsOfService;
